import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import api from "@/services/api";

export interface Employee {
  id: string;
  name: string;
  email: string;
  department: string;
  role: string;
  joinDate: string;
  status: "active" | "inactive";
}

export interface EmployeeFilter {
  department?: string;
  status?: string;
  searchTerm?: string;
}

export interface EmployeeSort {
  field: keyof Employee | null;
  direction: "asc" | "desc";
}

interface EmployeesState {
  items: Employee[];
  loading: boolean;
  error: string | null;
  filter: EmployeeFilter;
  sort: EmployeeSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: EmployeesState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

const normalizeEmployee = (payload: any): Employee => ({
  id: String(payload.id),
  name: payload.name,
  email: payload.email,
  department: payload.department,
  role: payload.role,
  joinDate: payload.joinDate,
  status: payload.status,
});

export const fetchEmployees = createAsyncThunk(
  "employees/fetchEmployees",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/employees");
      const raw = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
      return raw.map(normalizeEmployee);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employees");
    }
  }
);

export const createEmployee = createAsyncThunk(
  "employees/createEmployee",
  async (
    data: {
      name: string;
      email: string;
      department: string;
      role: string;
      joinDate: string;
      status?: "active" | "inactive";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post("/v1/employees", {
        name: data.name,
        email: data.email,
        department: data.department,
        role: data.role,
        join_date: data.joinDate,
        status: data.status ?? "active",
      });
      return normalizeEmployee(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create employee");
    }
  }
);

export const updateEmployee = createAsyncThunk(
  "employees/updateEmployee",
  async (
    { id, data }: {
      id: string;
      data: {
        name: string;
        email: string;
        department: string;
        role: string;
        joinDate: string;
        status: "active" | "inactive";
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/v1/employees/${id}`, {
        name: data.name,
        email: data.email,
        department: data.department,
        role: data.role,
        join_date: data.joinDate,
        status: data.status,
      });
      return normalizeEmployee(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update employee");
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  "employees/deleteEmployee",
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/employees/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete employee");
    }
  }
);

const employeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    setEmployeeFilter: (state, action: PayloadAction<EmployeeFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setEmployeeSort: (state, action: PayloadAction<EmployeeSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setEmployeePage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setEmployeePageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetEmployeeFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addEmployee: (state, action: PayloadAction<Employee>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateEmployeeItem: (state, action: PayloadAction<Employee>) => {
      const index = state.items.findIndex((e) => e.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteEmployeeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((e) => e.id !== action.payload);
      state.total -= 1;
    },
    resetEmployees: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = action.payload.length;
        state.error = null;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const index = state.items.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.items = state.items.filter((e) => e.id !== action.payload);
        state.total -= 1;
      });
  },
});

export const {
  setEmployeeFilter,
  setEmployeeSort,
  setEmployeePage,
  setEmployeePageSize,
  resetEmployeeFilters,
  addEmployee,
  updateEmployeeItem,
  deleteEmployeeItem,
  resetEmployees,
} = employeesSlice.actions;
export default employeesSlice.reducer;

// Selectors
export const selectEmployees = (state: any) => state.employees.items;
export const selectEmployeesLoading = (state: any) => state.employees.loading;
export const selectEmployeesError = (state: any) => state.employees.error;
export const selectEmployeeFilter = (state: any) => state.employees.filter;
export const selectEmployeeSort = (state: any) => state.employees.sort;
export const selectEmployeePagination = (state: any) => ({
  currentPage: state.employees.currentPage,
  pageSize: state.employees.pageSize,
  total: state.employees.total,
});

export const selectFilteredEmployees = createSelector(
  [(state: any) => state.employees.items, (state: any) => state.employees.filter, (state: any) => state.employees.sort, (state: any) => state.employees.currentPage, (state: any) => state.employees.pageSize],
  (items, filter, sort, currentPage, pageSize) => {
    let filtered = [...items];

    if (filter.department) {
      filtered = filtered.filter((e) => e.department === filter.department);
    }

    if (filter.status) {
      filtered = filtered.filter((e) => e.status === filter.status);
    }

    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter((e) =>
        e.name.toLowerCase().includes(term) ||
        e.email.toLowerCase().includes(term) ||
        e.role.toLowerCase().includes(term)
      );
    }

    if (sort.field) {
      filtered.sort((a, b) => {
        const aValue = a[sort.field!];
        const bValue = b[sort.field!];

        if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      items: filtered.slice(startIndex, endIndex),
      total: filtered.length,
    };
  }
);
